import React, { useMemo, useState } from 'react';
import { buildSessionQuality, slotLabelOf, matchConfidence } from '../utils/sessionQuality';

/**
 * SessionQualityAlert — passback/sentinel incidents and slow-creative events, so QA
 * can see at a glance how many revenue opportunities were lost and to whom.
 * Shared by the Lifecycle and Slots tabs (data: utils/sessionQuality.js).
 *
 * onSelect(lc) — optional; when given, each listed slot is a button that opens it.
 * hint         — optional footer line telling the reader where to go next.
 */

const CONF = {
  exact: { label: 'exact', bg: 'var(--success-bg)', fg: 'var(--success-text)', tip: (t) => `Impression matched to this GAM by ${t} (deterministic) — delay is reliable.` },
  adUnit: { label: '≈ ad unit', bg: 'var(--warn-bg)', fg: 'var(--warn-text)', tip: () => 'Impression matched by ad unit + time proximity — verify if this iu refreshes often.' },
  nearest: { label: '≈ nearest', bg: 'var(--error-bg)', fg: 'var(--error-text)', tip: () => 'Impression matched by nearest-in-time only (no structural signal) — lowest confidence; may be mis-bound on repeated iu.' },
};

const ROW = {
  display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap',
  background: 'var(--card)', borderRadius: 4, padding: '3px 8px', marginBottom: 2, fontSize: 11,
  border: 'none', width: '100%', textAlign: 'left', font: 'inherit', color: 'inherit',
};
const MORE_BTN = {
  background: 'none', border: 'none', padding: '2px 0', cursor: 'pointer',
  color: 'var(--error-text)', fontSize: 11, fontWeight: 600, textDecoration: 'underline',
};
const LABEL = { fontWeight: 600, color: 'var(--error-text)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

function SlotLine({ lc, onSelect, style, children }) {
  if (!onSelect) return <div style={{ ...ROW, ...style }}>{children}</div>;
  return (
    <button type="button" onClick={() => onSelect(lc)} title="Open this slot" style={{ ...ROW, cursor: 'pointer', ...style }}>
      {children}
    </button>
  );
}

export default function SessionQualityAlert({ lifecycles, onSelect, hint }) {
  const q = useMemo(() => buildSessionQuality(lifecycles), [lifecycles]);
  const [showAllPassbacks, setShowAllPassbacks] = useState(false);
  const [showAllSlow, setShowAllSlow] = useState(false);
  const [showUncertainSlow, setShowUncertainSlow] = useState(false);
  if (q.empty) return null;
  const { passbacks, passbackSlow, slowCreatives, slowUncertain, bidderSummary, totalCpm, hasRealIncident } = q;

  // Red incident styling only when there's a real (confident) incident;
  // a "needs verification"-only panel stays neutral so it doesn't look alarming.
  return (
    <div data-testid="session-quality" style={{
      background: hasRealIncident ? 'var(--error-bg)' : 'var(--card)',
      border: `1px solid ${hasRealIncident ? 'var(--error-border)' : 'var(--line)'}`,
      borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <span style={{ flexShrink: 0, fontSize: 14, marginTop: 1 }}>{hasRealIncident ? '⚠️' : '🔍'}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          {passbacks.length > 0 && (
            <>
              <div style={{ fontWeight: 700, color: 'var(--error-text)', marginBottom: 4 }}>
                {passbacks.length} passback incident{passbacks.length !== 1 ? 's' : ''} this session
                {totalCpm > 0 && ` — $${totalCpm.toFixed(2)} CPM at risk`}
                <span style={{ fontWeight: 400, marginLeft: 6 }}>({bidderSummary})</span>
              </div>
              {(showAllPassbacks ? passbacks : passbacks.slice(0, 5)).map((lc, i) => {
                const w = lc.phases.prebid.winner;
                const blankSec = passbackSlow.includes(lc) ? (lc.phases.impression.latencyMs / 1000).toFixed(1) : null;
                return (
                  <SlotLine key={i} lc={lc} onSelect={onSelect}>
                    <span style={LABEL}>{slotLabelOf(lc)}</span>
                    <span style={{ color: 'var(--error-text)', flexShrink: 0 }}>{w.seat} {w.size} — ${Number(w.price).toFixed(2)} passback</span>
                    {blankSec && <span style={{ color: 'var(--warn-text)', flexShrink: 0 }}>· blank {blankSec}s</span>}
                  </SlotLine>
                );
              })}
              {passbacks.length > 5 && (
                <button type="button" onClick={() => setShowAllPassbacks((v) => !v)} style={MORE_BTN}>
                  {showAllPassbacks ? '▲ Show less' : `▼ Show ${passbacks.length - 5} more…`}
                </button>
              )}
            </>
          )}
          {slowCreatives.length > 0 && (
            <div style={{ marginTop: passbacks.length > 0 ? 6 : 0 }}>
              <div style={{ color: 'var(--error-text)', lineHeight: 1.6, marginBottom: 4 }}>
                🐢 {slowCreatives.length} slot{slowCreatives.length !== 1 ? 's' : ''} with creative render &gt;30s — ad was not displayed despite slot being viewable
              </div>
              {(showAllSlow ? slowCreatives : slowCreatives.slice(0, 5)).map((lc, i) => {
                const latSec = (lc.phases.impression.latencyMs / 1000).toFixed(1);
                const demand = lc.phases?.gam?.adapterLabel || lc.phases?.gam?.hbBidder || 'AdX';
                // Render delay is only as trustworthy as the impression↔GAM join.
                const tier = lc.phases?.impression?.matchTier;
                const conf = CONF[matchConfidence(tier)];
                return (
                  <SlotLine key={i} lc={lc} onSelect={onSelect} style={{ flexWrap: 'nowrap' }}>
                    <span style={LABEL}>{slotLabelOf(lc)}</span>
                    <span style={{ color: 'var(--error-text)', flexShrink: 0 }}>{latSec}s render delay</span>
                    <span title={conf.tip(tier)} style={{ flexShrink: 0, fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 999, background: conf.bg, color: conf.fg }}>{conf.label}</span>
                    {demand && <span style={{ color: 'var(--warn-text)', flexShrink: 0 }}>· {demand}</span>}
                    {lc.phases?.gam?.winnerCpm && <span style={{ color: 'var(--warn-text)', flexShrink: 0 }}>· ${Number(lc.phases.gam.winnerCpm).toFixed(2)} CPM</span>}
                  </SlotLine>
                );
              })}
              {slowCreatives.length > 5 && (
                <button type="button" onClick={() => setShowAllSlow((v) => !v)} style={MORE_BTN}>
                  {showAllSlow ? '▲ Show less' : `▼ Show ${slowCreatives.length - 5} more…`}
                </button>
              )}
            </div>
          )}
          {/* Low-confidence gaps — surfaced for review, NOT counted as incidents */}
          {slowUncertain.length > 0 && (
            <div style={{ marginTop: (passbacks.length > 0 || slowCreatives.length > 0) ? 8 : 0 }}>
              <button type="button" onClick={() => setShowUncertainSlow((v) => !v)} style={{
                background: 'none', border: 'none', padding: '2px 0', cursor: 'pointer',
                color: 'var(--muted)', fontSize: 11, fontWeight: 600, textAlign: 'left',
              }}>
                {showUncertainSlow ? '▲' : '▼'} 🔍 {slowUncertain.length} slot{slowUncertain.length !== 1 ? 's' : ''} with a &gt;30s gap to impression — needs verification (matched by nearest-time only · not counted)
              </button>
              {showUncertainSlow && (
                <div style={{ marginTop: 4 }}>
                  {slowUncertain.map((lc, i) => {
                    const latSec = (lc.phases.impression.latencyMs / 1000).toFixed(1);
                    const demand = lc.phases?.gam?.adapterLabel || lc.phases?.gam?.hbBidder || 'AdX';
                    return (
                      <SlotLine key={i} lc={lc} onSelect={onSelect} style={{ flexWrap: 'nowrap', background: 'var(--bg)', border: '1px dashed var(--line)', color: 'var(--muted)' }}>
                        <span style={{ ...LABEL, color: 'inherit' }}>{slotLabelOf(lc)}</span>
                        <span style={{ flexShrink: 0 }}>{latSec}s gap</span>
                        <span title="Impression matched by nearest-in-time only (no structural signal) — may be mis-bound on a repeated ad unit." style={{ flexShrink: 0, fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 999, background: 'var(--neutral-bg)', color: 'var(--muted)', border: '1px solid var(--line)' }}>≈ nearest</span>
                        {demand && <span style={{ flexShrink: 0 }}>· {demand}</span>}
                      </SlotLine>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          {hint ? (
            <div style={{ fontSize: 11, color: 'var(--error-text)', marginTop: 5, paddingTop: 5, borderTop: '1px solid var(--error-border)' }}>
              💡 {hint}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
